import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { blogPosts, categories } from '../data/blogData';
import { 
  Search, 
  Clock, 
  User, 
  MapPin, 
  Stethoscope, 
  Heart, 
  Brain, 
  Baby, 
  Scissors,
  ShieldAlert,
  Pill,
  TestTube,
  TrendingUp,
  Shield,
  Eye,
  Zap,
  Activity,
  Briefcase,
  Users,
  Star,
  Target,
  Phone
} from 'lucide-react';

const Blogs = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Optimized Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Expert Medical Insights from 20 Doctors Across Andhra Pradesh & Telangana
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Get expert medical advice from GEETH365's network of 20 specialist doctors across 10 cities including Visakhapatnam (530041), Rajahmundry (533103), Guntur (522002), Hyderabad (500085), and more in Andhra Pradesh & Telangana.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by doctor, city, pincode, or medical specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-lg py-3 border-orange-200 focus:border-orange-400"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Medical Specialties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-orange-100 text-orange-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{category.name}</span>
                      <Badge variant="outline">{category.count}</Badge>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="hover:shadow-lg transition-shadow border-orange-100">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className={`${post.color} p-3 rounded-full flex-shrink-0`}>
                        <post.icon className="w-6 h-6" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-3">
                          <Badge className="bg-orange-100 text-orange-700">{post.category}</Badge>
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-1" />
                            {post.readTime}
                          </div>
                        </div>
                        
                        <Link to={`/blog/${post.slug}`}>
                          <h2 className="text-xl font-bold text-gray-900 mb-3 hover:text-orange-600 transition-colors">
                            {post.title}
                          </h2>
                        </Link>
                        
                        <p className="text-gray-600 mb-4 leading-relaxed">
                          {post.excerpt}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 4).map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <User className="w-4 h-4 mr-1" />
                            <span>{post.author}</span>
                            <span className="mx-2">•</span>
                            <span>{post.date}</span>
                          </div>
                          
                          <button className="text-orange-600 hover:text-orange-700 font-medium text-sm">
                            Read Expert Advice →
                          </button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* SEO Footer Content */}
            <div className="mt-12 p-6 bg-white rounded-lg border border-orange-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                GEETH365 Healthcare Network Across Andhra Pradesh & Telangana
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Andhra Pradesh Cities</h4>
                  <ul className="space-y-1">
                    <li>Visakhapatnam (530041) - 7 Doctors</li>
                    <li>Rajahmundry (533103-533104) - 3 Doctors</li>
                    <li>Guntur (522002) - 2 Doctors</li>
                    <li>Chittor (517001-517127) - 2 Doctors</li>
                    <li>Vijayawada (520008) - 1 Doctor</li>
                    <li>Nellore (524003) - 1 Doctor</li>
                    <li>Bhimavaram (534202) - 1 Doctor</li>
                    <li>Tanuku (534211) - 1 Doctor</li>
                    <li>Ganapavaram (534198) - 1 Doctor</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Telangana Coverage</h4>
                  <ul className="space-y-1">
                    <li>Hyderabad (500085) - Digital Health</li>
                    <li>Kukatpally - Telemedicine Center</li>
                    <li>All major Telangana cities covered</li>
                    <li>24/7 WhatsApp consultations</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Medical Specialties Available</h4>
                  <ul className="space-y-1">
                    <li>Cardiology & Heart Care</li>
                    <li>Critical Care & Emergency Medicine</li>
                    <li>Pediatrics & Child Healthcare</li>
                    <li>Diabetes & Endocrinology</li>
                    <li>Neurology & Brain Care</li>
                    <li>Dermatology & Skin Care</li>
                    <li>Women's Health & Gynecology</li>
                    <li>Surgery & Trauma Care</li>
                    <li>Pulmonology & Respiratory Care</li>
                    <li>Mental Health & Psychiatry</li>
                    <li>Eye Care & Ophthalmology</li>
                    <li>Medical Imaging & Radiology</li>
                    <li>Cancer Care & Oncology</li>
                    <li>Neonatal & Intensive Care</li>
                    <li>Internal Medicine</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Book Consultation with GEETH365 Doctors</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Get expert medical advice from our network of 20 specialist doctors across Andhra Pradesh & Telangana. 
                  WhatsApp booking available for immediate consultations.
                </p>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="font-medium">📱 WhatsApp: 7337541113</span>
                  <span className="font-medium">🏥 Available 24/7</span>
                  <span className="font-medium">💳 All Insurance Accepted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;